/**
 * useUrlHashRoute — bind a primary navigation selection to the URL hash with
 * real browser-history semantics.
 *
 * Where `useUrlHashState` persists ephemeral in-demo state (an active level,
 * panel, or filter) via `history.replaceState`, this hook owns the *route*:
 * the top-level selection a visitor would bookmark, share, or step through
 * with the browser Back and Forward buttons. Each navigation pushes a history
 * entry, so Back returns to the previously viewed selection rather than
 * leaving the surrounding page.
 *
 * Like `useUrlHashState`, exactly one hash key is owned by each instance and
 * other keys in the same hash (`#demo=x&level=2`) are preserved untouched, so
 * a route hook and several in-demo state hooks compose without collision. The
 * hash parse / serialize / commit primitives are shared between the two hooks
 * via `./urlHashParams.js`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useEffect, useState } from "react";
import { commitHashKey, readHashKey } from "./urlHashParams.js";

/**
 * Bind a single route value to `location.hash[key]`, pushing a browser
 * history entry on every navigation so Back and Forward step through the
 * visited selections.
 *
 * @param key      Hash parameter name (a valid URL parameter key).
 * @param fallback Value used when the hash is empty or invalid.
 * @param isValid  Optional guard rejecting unknown hash values.
 * @returns `[value, navigate]` — `navigate(next)` is a no-op when `next`
 *          already equals the current value, so repeated selection of the
 *          active route does not flood the history stack.
 */
export function useUrlHashRoute<T extends string>(
  key: string,
  fallback: T,
  isValid?: (value: string) => value is T,
): readonly [T, (value: T) => void] {
  const read = useCallback((): T => {
    const raw = readHashKey(key);
    if (raw === null) return fallback;
    if (isValid && !isValid(raw)) return fallback;
    return raw as T;
  }, [key, fallback, isValid]);

  const [value, setValue] = useState<T>(read);

  useEffect(() => {
    const handler = (): void => {
      setValue(read());
    };
    window.addEventListener("popstate", handler);
    window.addEventListener("hashchange", handler);
    return (): void => {
      window.removeEventListener("popstate", handler);
      window.removeEventListener("hashchange", handler);
    };
  }, [read]);

  const navigate = useCallback(
    (next: T): void => {
      // Selecting the already-active route is a no-op so repeated clicks do
      // not flood the history stack with identical entries.
      if (next === read()) return;
      commitHashKey(key, next, "push");
      setValue(next);
    },
    [key, read],
  );

  return [value, navigate] as const;
}
